import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import TemplateDetail from './TemplateDetail';

function TemplatesManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'ONBOARDING',
    roleTarget: 'CM',
    isActive: true,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.get('/templates');
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description,
        type: template.type,
        roleTarget: template.roleTarget || '',
        isActive: template.isActive,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        description: '',
        type: 'ONBOARDING',
        roleTarget: 'CM',
        isActive: true,
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.id}`, formData);
      } else {
        await api.post('/templates', formData);
      }
      await loadTemplates();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template and all its items?')) return;

    try {
      await api.delete(`/templates/${templateId}`);
      await loadTemplates();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (err) {
      alert('Failed to delete template: ' + err.message);
    }
  };

  const handleViewDetails = (template) => {
    setSelectedTemplate(template);
  };

  const handleBackToList = () => {
    setSelectedTemplate(null);
    loadTemplates(); // Refresh list
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (selectedTemplate) {
    return <TemplateDetail template={selectedTemplate} onBack={handleBackToList} />;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Template Management</h1>
        <p className="page-subtitle">Create and manage checklist templates</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Templates</h2>
          <button className="btn btn-primary" onClick={() => handleOpenModal()} data-testid="create-template-btn">
            + Create Template
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3 className="empty-state-title">No Templates Yet</h3>
            <p className="empty-state-description">Get started by creating your first checklist template.</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>Create Template</button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Template Name</th>
                  <th>Type</th>
                  <th>Role Target</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td style={{ fontWeight: 500 }}>{template.name}</td>
                    <td>
                      <span className="badge badge-info">{template.type}</span>
                    </td>
                    <td>{template.roleTarget || 'Any'}</td>
                    <td>
                      {template.isActive ? (
                        <span className="badge badge-success">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-primary btn-small"
                          onClick={() => handleViewDetails(template)}
                          data-testid={`view-template-${template.id}`}
                        >
                          View Details
                        </button>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleOpenModal(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDelete(template.id)}
                          data-testid={`delete-template-${template.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                
                <div className="form-group">
                  <label className="form-label" htmlFor="name">Template Name</label>
                  <input
                    id="name"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Community Manager Onboarding"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="template-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    className="form-input"
                    placeholder="Brief description of this template"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    required
                    data-testid="template-description-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="type">Type</label>
                  <select
                    id="type"
                    className="form-select"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                    data-testid="template-type-select"
                  >
                    <option value="ONBOARDING">Onboarding</option>
                    <option value="OPERATIONS">Operations</option>
                    <option value="AUDIT">Audit</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="roleTarget">Target Role (Optional)</label>
                  <select
                    id="roleTarget"
                    className="form-select"
                    value={formData.roleTarget}
                    onChange={(e) => setFormData({ ...formData, roleTarget: e.target.value })}
                    data-testid="template-roleTarget-select"
                  >
                    <option value="">Any Role</option>
                    <option value="CM">Community Manager</option>
                    <option value="AREA_MANAGER">Area Manager</option>
                    <option value="REGIONAL">Regional Director</option>
                    <option value="VP">VP</option>
                    <option value="EXEC">Executive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                      data-testid="template-isActive-checkbox"
                    />
                    <span className="form-label" style={{ marginBottom: 0 }}>Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal} data-testid="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-template-btn">
                  {editingTemplate ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplatesManagement;
